"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, FileText, Search, ShieldCheck, Sparkles, TrendingUp, Users, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const guideCoverage = [{
  id: 1,
  title: "Keyword Research & Search Intent",
  desc: "Find high-intent search terms your potential customers actually use.",
  icon: Search
}, {
  id: 2,
  title: "Technical SEO & Site Health",
  desc: "Identify crawl errors, broken links, indexing issues, and speed bottlenecks.",
  icon: Wrench
}, {
  id: 3,
  title: "On-Page Content Optimisation",
  desc: "Optimise titles, headings, and copy to rank higher on Google.",
  icon: FileText
}, {
  id: 4,
  title: "Rank Tracking & Visibility",
  desc: "Monitor keyword rankings across target locations and track your growth.",
  icon: TrendingUp
}, {
  id: 5,
  title: "Competitor Analysis",
  desc: "Uncover competitors' top keywords, ranking pages, and content gaps.",
  icon: Users
}];
export default function Seoguide() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const staggerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <section id="editorialheroseoguide" data-nav="light" className="bg-background text-foreground relative py-12 md:py-20 lg:py-24 overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-primary/5 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 -left-20 size-80 rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Hero Copy & Actions */}
          <motion.div className="lg:col-span-7 flex flex-col items-start text-left" initial="hidden" animate="visible" variants={staggerVariants}>
            <motion.div variants={fadeInVariants} className="mb-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1.5 text-xs font-medium text-secondary-foreground border border-border shadow-xs">
                <ShieldCheck className="size-4 text-primary" />
                <span>Expert Guide &bull; By Yassine Essaadi</span>
                <span className="text-muted-foreground hidden sm:inline">
                  | Updated 2025
                </span>
              </div>
            </motion.div>

            <motion.h1 variants={fadeInVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-6">
              DIY SEO Software: The Complete Guide to Managing Your Own SEO
            </motion.h1>

            <motion.div variants={fadeInVariants} className="flex items-center gap-3 mb-6 p-2 pr-4 rounded-full bg-muted/50 border border-border">
              <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-srcset="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png-wvc-srcset" data-wvc-sizes="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" loading="lazy" decoding="async" className="size-10 rounded-full object-cover border border-border" />
              <div className="text-xs">
                <p className="font-semibold text-foreground">
                  Written by Yassine Essaadi
                </p>
                <p className="text-muted-foreground">
                  Independent Freelance SEO Consultant
                </p>
              </div>
            </motion.div>

            <motion.p variants={fadeInVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              Small business owners in the UK typically spend £500 to £1,000+
              each month on agency retainers with little clarity on where that
              budget goes. In my work as an independent SEO consultant, I have
              seen that modern{" "}
              <strong className="text-foreground font-semibold">
                DIY SEO software
              </strong>{" "}
              can deliver comparable capability — keyword research, technical
              audits, on-page content optimisation, rank tracking, and competitor
              analysis — at a fraction of the cost, giving you complete
              transparency and ownership over your search growth.
            </motion.p>

            <motion.div variants={fadeInVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-7 py-6 text-base font-semibold shadow-md transition-colors" asChild>
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-muted rounded-lg px-7 py-6 text-base font-semibold transition-colors" asChild>
                <Link to="/SeoAuditsPage" className="inline-flex items-center justify-center gap-2">
                  <span>Get a Free SEO Audit</span>
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={fadeInVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full pt-4 border-t border-border/60 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>100% Independent Advice</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Direct Consultant Support</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Actionable Step-by-Step Guidance</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column: Guide Overview Card */}
          <motion.div className="lg:col-span-5" initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 25
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
            <div className="bg-card text-card-foreground border border-border shadow-xl rounded-2xl p-6 sm:p-8 relative">
              <div className="absolute -top-3 right-6">
                <Badge className="bg-accent text-accent-foreground border-none px-3 py-1 text-xs font-semibold shadow-xs flex items-center gap-1.5 rounded-full">
                  <Sparkles className="size-3" />
                  <span>Comprehensive Guide</span>
                </Badge>
              </div>

              <div className="flex items-center gap-2 text-primary font-serif font-bold text-lg mb-2">
                <BookOpen className="size-5" />
                <span>What This Guide Covers</span>
              </div>

              <h2 className="font-serif text-2xl font-bold text-foreground mb-3">
                Core DIY SEO Capabilities
              </h2>

              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Everything you need to manage, execute, and monitor your
                in-house search engine optimisation without paying high agency fees:
              </p>

              <div className="space-y-4 mb-6">
                {guideCoverage.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.id} data-index={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/40 border border-border/60">
                      <div className="size-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="size-4" />
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-foreground">
                          {item.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              <div className="p-3.5 rounded-lg bg-secondary/50 border border-border text-xs text-secondary-foreground leading-relaxed">
                <p className="font-medium">
                  <span className="font-semibold text-foreground">
                    Consultant Note:
                  </span>{" "}
                  You can run day-to-day SEO in-house with dedicated software, and bring me in for periodic deep-dive audits or strategic reviews when you need specialist guidance.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}